import { ZOOM_LEVELS } from 'features/mapping/mapping.constants';
import { MAP_ENTITY_DRAWER_WIDTH } from 'features/mapping/components/MapEntityDrawer';
import { MapRef } from 'shared/map-exports';
import { LatLng } from 'shared/types';
import {
  getSearchCardMinSnapPx,
  SEARCH_DRAWER_WIDTH,
} from '../../../../constants';

export type CenterMapOptions = {
  /** If true, prevents zooming out below the current map zoom level */
  zoomIn?: boolean;
  /**
   * Target zoom level. If provided alongside `zoomIn`, the current map zoom
   * is respected when it is higher than this value.
   */
  zoom?: number;
  /** Shifts the center to clear the drawer overlay. */
  drawerIsOpen?: boolean;
  /** Mobile bottom sheet height, as a fraction of the viewport. */
  defaultSnapPointPct: number;
};

const getXOffsetForDesktop = (input: {
  isLargeMediaQuery: boolean;
  drawerIsOpen: boolean;
  isSearchDrawerOpen: boolean;
}): number => {
  const { isLargeMediaQuery, drawerIsOpen, isSearchDrawerOpen } = input;
  if (!isLargeMediaQuery) {
    return 0;
  }
  if (drawerIsOpen) {
    const offsetWidth = isSearchDrawerOpen
      ? MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
      : MAP_ENTITY_DRAWER_WIDTH;
    return -Math.round(offsetWidth / 2);
  }
  return 0;
};

const getYOffsetForMobile = (
  isLargeMediaQuery: boolean,
  drawerIsOpen: boolean,
  defaultSnapPointPct: number,
): number => {
  if (isLargeMediaQuery) {
    return 0;
  }
  if (drawerIsOpen) {
    return -Math.round((window.innerHeight * defaultSnapPointPct) / 2);
  }

  return -Math.round(getSearchCardMinSnapPx() / 2);
};

/**
 * Offset that keeps the centered point clear of the overlay (side drawer on
 * desktop, bottom sheet on mobile). Computed at call time so it always
 * reflects the current drawer state and viewport dimensions.
 */
export const getCenterOffset = (input: {
  isLargeMediaQuery: boolean;
  drawerIsOpen: boolean;
  isSearchDrawerOpen: boolean;
  defaultSnapPointPct: number;
}): [number, number] => {
  const {
    isLargeMediaQuery,
    drawerIsOpen,
    isSearchDrawerOpen,
    defaultSnapPointPct,
  } = input;
  return [
    getXOffsetForDesktop({
      isLargeMediaQuery,
      drawerIsOpen,
      isSearchDrawerOpen,
    }),
    getYOffsetForMobile(isLargeMediaQuery, drawerIsOpen, defaultSnapPointPct),
  ];
};

/** Resolves the zoom to center at, honoring the `zoom`/`zoomIn`/`skipZoom` props. */
export const getTargetZoom = (input: {
  map: MapRef;
  zoom?: number;
  zoomIn?: boolean;
  skipZoom: boolean;
}): number => {
  const { map, zoom, zoomIn, skipZoom } = input;
  if (skipZoom) return map.getZoom();
  // If both zoom and zoomIn are set, respect the current map zoom when it is
  // higher than the provided zoom to avoid unnecessary zooming out.
  if (zoom && zoomIn) return Math.max(zoom, map.getZoom());
  if (zoom) return zoom;
  return zoomIn ? Math.max(ZOOM_LEVELS.CITY, map.getZoom()) : map.getZoom();
};

/** Eases the map to a coordinate, cancelling any in-flight camera move first. */
export const centerMapOnLatLng = (input: {
  map: MapRef;
  latLng: LatLng;
  zoom: number;
  offset: [number, number];
}): void => {
  const { map, latLng, zoom, offset } = input;
  // Cancel any in-flight camera move so this target applies immediately.
  map.stop();
  map.easeTo({
    center: [latLng.lng, latLng.lat],
    zoom,
    linear: true,
    pitch: map.getPitch(),
    offset,
  });
};
